/**
 * The navigator user guide (docs.cancerexpert.now) is for staff only. The app's session
 * cookie stays on its own host, so the app hands a signed pass to the docs site instead:
 * a short-lived one in the redirect, which the docs site exchanges for its own cookie.
 *
 * A pass is `base64url(json).base64url(hmac-sha256)` over the JSON, with the account, an
 * expiry and a purpose, so a redirect pass can never serve as a cookie or the reverse.
 */
export type DocsPassPurpose = 'handoff' | 'session';

export interface DocsPass {
  /** Account id of the navigator. */
  sub: string;
  /** Expiry, milliseconds since the epoch. */
  exp: number;
  use: DocsPassPurpose;
}

/** The redirect pass lives one minute; the docs cookie one hour, then a silent new handoff. */
export const DOCS_PASS_TTL_MS: Record<DocsPassPurpose, number> = {
  handoff: 60_000,
  session: 3_600_000,
};

const enc = new TextEncoder();

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function fromB64url(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

async function key(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
    'verify',
  ]);
}

export async function signDocsPass(
  secret: string,
  sub: string,
  use: DocsPassPurpose,
  now: Date,
): Promise<string> {
  const pass: DocsPass = { sub, exp: now.getTime() + DOCS_PASS_TTL_MS[use], use };
  const body = b64url(enc.encode(JSON.stringify(pass)));
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', await key(secret), enc.encode(body)));
  return `${body}.${b64url(sig)}`;
}

/** The pass if its signature, purpose and expiry hold; otherwise null. */
export async function verifyDocsPass(
  secret: string,
  token: string,
  use: DocsPassPurpose,
  now: Date,
): Promise<DocsPass | null> {
  const [body, sig, extra] = token.split('.');
  if (!body || !sig || extra !== undefined) return null;
  let ok = false;
  try {
    ok = await crypto.subtle.verify('HMAC', await key(secret), fromB64url(sig), enc.encode(body));
  } catch {
    return null;
  }
  if (!ok) return null;
  let pass: DocsPass;
  try {
    pass = JSON.parse(new TextDecoder().decode(fromB64url(body))) as DocsPass;
  } catch {
    return null;
  }
  if (pass.use !== use || typeof pass.sub !== 'string' || typeof pass.exp !== 'number') return null;
  if (pass.exp <= now.getTime()) return null;
  return pass;
}

/** A path on the docs site to return to after the handoff; anything else falls back to `/`. */
export function docsReturnPath(input: string | null | undefined): string {
  if (!input || !input.startsWith('/') || input.startsWith('//') || input.includes('\\')) return '/';
  return input.slice(0, 500);
}
